/**
 * TaskDetailsContainer Component
 *
 * Main orchestrator for the Task Details view.
 * Composes sub-components and manages shared state/logic.
 *
 * Decomposed from TaskDetailsView.js (1,664 lines)
 * This container handles:
 * - Data fetching and state management
 * - Event handlers for updates
 * - Composition of sub-components
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Col, Modal, Button, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { withRouter, useParams } from 'react-router-dom';
import { debounce, startCase } from 'lodash';
import moment from 'moment';

import { PageContentLayout } from '../../../../shared/layout';
import OverlaySpinner from '../../../../shared/OverlaySpinner';
import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import { useUserContext } from '../../../../../core/components/users/useUserContext';
import { useOrgContext } from '../../../../../core/components/orgs/useOrgContext';
import useResponsiveTask from '../../hooks/useResponsiveTask';
import { CHANNEL_PARTNER_LEVELS } from '../../utils/task.utils';

import TaskDetailsHeader from './TaskDetailsHeader';
import TaskDetailsTabs from './TaskDetailsTabs';
import TaskDetailsFollowers from './TaskDetailsFollowers';
import TaskDetailsMetadata from './TaskDetailsMetadata';
import TaskDetailsAssignees from './TaskDetailsAssignees';
import TaskDetailsMobileSummary from './TaskDetailsMobileSummary';
import TaskDetailsBottomSheet from './TaskDetailsBottomSheet';
import TaskDetailsSheetContent from './TaskDetailsSheetContent';
import TaskFloatingCommentInput from './TaskFloatingCommentInput';
import useBottomSheet from './hooks/useBottomSheet';

import '../../styles/index.scss';

/**
 * Decides which view the task details page should show, given the requested id and the
 * (provider-global, persistent) task query state. Pure so the state machine can be unit
 * tested without rendering — see TaskDetailsContainer.test.js.
 *
 * @returns {'no-selection'|'loading'|'missing'|'content'}
 *  - 'loading': no matching task yet and the fetch hasn't settled empty (cold mount,
 *    in-flight, or a stale result still held from a previously viewed task).
 *  - 'missing': the fetch for THIS id finished with no record (deleted/invalid id, no
 *    access, or an error). `requestedTaskId === currentTaskId` ensures the empty result
 *    belongs to the current id and not a previous one.
 *  - 'content': the matching task is in hand (even during a background refetch — no flicker).
 */
export const getTaskDetailsView = ({ currentTaskId, currentTask, taskLoading, requestedTaskId }) => {
  if (!currentTaskId) return 'no-selection';
  if (currentTask && currentTask.id === currentTaskId) return 'content';
  if (!taskLoading && !currentTask && requestedTaskId === currentTaskId) return 'missing';
  return 'loading';
};

const EMPTY_DETAILS_FORM = {
  urgency: null,
  startDate: null,
  dueDate: null,
  requestType: null,
  requestSubType: null,
  channelPartnerId: null,
  advertiserId: null,
};

const buildDetailsFormFromTask = (task) => {
  if (!task) return { ...EMPTY_DETAILS_FORM };
  return {
    urgency: task.urgency || null,
    startDate: task.startDate ? moment(task.startDate) : null,
    dueDate: task.dueDate ? moment(task.dueDate) : null,
    requestType: task.requestType || null,
    requestSubType: task.requestSubType || null,
    channelPartnerId: task.channelPartner?.id || null,
    advertiserId: task.advertiser?.id || null,
  };
};

const TaskDetailsContainer = ({ history }) => {
  const { id: currentTaskId } = useParams();
  const [activeTab, setActiveTab] = useState('1');
  const [isEditingAssignee, setIsEditingAssignee] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [detailsForm, setDetailsForm] = useState({ ...EMPTY_DETAILS_FORM });
  const [channelPartnerOrgs, setChannelPartnerOrgs] = useState([]);
  const [advertiserOrgs, setAdvertiserOrgs] = useState([]);
  const [orgTypeRequest, setOrgTypeRequest] = useState(null);
  const { width: windowWidth, isMobile, isDesktop } = useResponsiveTask();

  // Bottom sheet for mobile
  const {
    currentHeight,
    isDragging,
    isOpen: isSheetOpen,
    open: openSheet,
    close: closeSheet,
    toggle: toggleSheet,
    dragHandleProps,
  } = useBottomSheet({
    snapPoints: [0, 95],
    initialSnap: 0,
  });

  // Task context
  const {
    currentUser,
    updateTask,
    updateTaskLoading,
    currentTask,
    fetchTask,
    taskLoading,
    taskError,
    fetchHistories,
    histories,
    loadingHistories,
    refetchTask,
    fetchTeams,
    teamsData,
    teamsLoading,
  } = useTaskContext();

  // Org context
  const { fetchBasicOrgsDetails, basicOrgsDetails, basicOrgsDetailsLoading } = useOrgContext();

  // User context
  const {
    fetchBasicUsersDetails,
    basicUsersDetails,
    basicUsersDetailsLoading,
    fetchCollaboratorsUsers,
    collaboratorsData,
    collaboratorsLoading,
  } = useUserContext();

  // Local state
  const [selectedOrg, setSelectedOrg] = useState(
    (currentTask && currentTask.assigneeOrg && currentTask.assigneeOrg.id) || null
  );
  const [selectedAssignees, setSelectedAssignees] = useState(
    (currentTask &&
      currentTask.assigneeUsers &&
      currentTask.assigneeUsers[0] &&
      currentTask.assigneeUsers[0].id) ||
      []
  );
  const [selectedCollaborators, setSelectedCollaborators] = useState(
    (currentTask &&
      currentTask.collaborators &&
      currentTask.collaborators.map((user) => user.id)) ||
      []
  );
  const [isFollowers, setIsFollowers] = useState(
    currentTask &&
      currentTask.followers &&
      currentUser &&
      currentTask.followers.some((f) => f.id === currentUser.id)
  );
  const [isCollaborator, setIsCollaborator] = useState(
    currentTask &&
      currentTask.collaborators &&
      currentUser &&
      currentTask.collaborators.some((c) => c.id === currentUser.id)
  );
  const [showFollowerModal, setShowFollowerModal] = useState(false);
  const [selectedFollowerId, setSelectedFollowerId] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(
    currentTask?.teams?.[0]?.id || null
  );
  const [teams, setTeams] = useState([]);

  const isCurrentUserAssignee =
    currentTask && currentTask.assigneeUsers
      ? currentTask.assigneeUsers.some((user) => user.id === currentUser.id)
      : false;

  const level =
    currentUser && currentUser.role && currentUser.role.org && currentUser.role.org.level;

  // Sync state with currentTask
  useEffect(() => {
    if (currentTask) {
      setSelectedOrg(
        currentTask.assigneeOrg && currentTask.assigneeOrg.id ? currentTask.assigneeOrg.id : null
      );
      setSelectedAssignees(
        currentTask.assigneeUsers
          ? currentTask.assigneeUsers &&
              currentTask.assigneeUsers[0] &&
              currentTask.assigneeUsers[0].id
          : []
      );
      setIsFollowers(
        currentTask.followers &&
          currentUser &&
          currentTask.followers.some((f) => f.id === currentUser.id)
      );
      setIsCollaborator(
        currentTask &&
          currentTask.collaborators &&
          currentUser &&
          currentTask.collaborators.some((c) => c.id === currentUser.id)
      );
      setSelectedCollaborators(
        currentTask && currentTask.collaborators && currentTask.collaborators.length > 0
          ? currentTask.collaborators.map((user) => user.id)
          : []
      );
      setSelectedTeamId(currentTask.teams?.[0]?.id || null);
      if (!isEditingDetails) {
        setDetailsForm(buildDetailsFormFromTask(currentTask));
        setChannelPartnerOrgs((prev) => {
          const cp = currentTask.channelPartner;
          if (!cp) return prev;
          return prev.some((o) => o.id === cp.id) ? prev : [...prev, cp];
        });
        setAdvertiserOrgs((prev) => {
          const adv = currentTask.advertiser;
          if (!adv) return prev;
          return prev.some((o) => o.id === adv.id) ? prev : [...prev, adv];
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTask, currentUser]);

  // Debounced fetch functions
  const fetchCollaborators = debounce((name = '') => {
    fetchCollaboratorsUsers({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: {
                  OR: [
                    {
                      parentOrg_some: {
                        id: currentUser.role.org.id,
                      },
                    },
                    {
                      id: currentUser.role.org.id,
                    },
                  ],
                  level: 'PLATFORM',
                },
                roleItems_some: {
                  feature: 'TASK_MANAGEMENT',
                },
              },
              name_contains: name,
            },
          ],
        },
      },
    });
  }, 500);

  const fetchBasicUsers = debounce((name = '') => {
    if (selectedOrg === null) {
      setSelectedAssignees([]);
      return;
    }
    fetchBasicUsersDetails({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: {
                  id: selectedOrg,
                  level: 'PLATFORM',
                },
                roleItems_some: {
                  feature: 'TASK_MANAGEMENT',
                },
              },
              name_contains: name,
            },
          ],
        },
      },
    });
  }, 500);

  const fetchBasicOrgs = debounce((name = '') => {
    setOrgTypeRequest('ASSIGNEE');
    fetchBasicOrgsDetails({
      variables: {
        first: 10,
        skip: 0,
        where: {
          AND: [
            {
              OR: [
                { parentOrg_some: { id: currentUser.role.org.id } },
                { id: currentUser.role.org.id },
              ],
              name_contains: name,
            },
          ],
          OR: [
            {
              roles_some: {
                org: {
                  level: 'PLATFORM',
                },
                roleItems_some: {
                  feature: 'TASK_MANAGEMENT',
                },
              },
            },
          ],
        },
      },
    });
  }, 500);

  const fetchTeamsByOrg = debounce((orgId, name = '') => {
    if (!orgId) {
      setTeams([]);
      return;
    }
    fetchTeams({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            { org: { id: orgId } },
            ...(name ? [{ name_contains: name }] : []),
          ],
        },
      },
    });
  }, 500);

  const fetchChannelPartnerOrgs = debounce((name = '') => {
    setOrgTypeRequest('CHANNEL_PARTNER');
    fetchBasicOrgsDetails({
      variables: {
        first: 300,
        skip: 0,
        where: {
          AND: [{ level_in: CHANNEL_PARTNER_LEVELS }, { name_contains: name }],
        },
      },
    });
  }, 500);

  const fetchAdvertiserOrgs = debounce((name = '', channelPartnerIdArg) => {
    const channelPartnerId =
      level !== 'PLATFORM'
        ? currentUser.role.org.id
        : channelPartnerIdArg || detailsForm.channelPartnerId;
    if (!channelPartnerId) {
      setAdvertiserOrgs([]);
      return;
    }
    setOrgTypeRequest('ADVERTISER');
    fetchBasicOrgsDetails({
      variables: {
        first: 1000,
        skip: 0,
        where: {
          AND: [
            {
              OR: [{ parentOrg_some: { id: channelPartnerId } }, { id: channelPartnerId }],
            },
            { name_contains: name },
          ],
        },
      },
    });
  }, 500);

  // Update teams when fetched, filtering out soft-deleted
  useEffect(() => {
    if (teamsData?.teams) {
      setTeams(teamsData.teams.filter((t) => !t.name.startsWith('__deleted__')));
    }
  }, [teamsData]);

  // Route fetched org list into the correct typed state bucket
  useEffect(() => {
    if (!orgTypeRequest) return;
    if (orgTypeRequest === 'CHANNEL_PARTNER') {
      setChannelPartnerOrgs(basicOrgsDetails || []);
    } else if (orgTypeRequest === 'ADVERTISER') {
      setAdvertiserOrgs(basicOrgsDetails || []);
    }
    // ASSIGNEE: assignee selects read basicOrgsDetails directly, no copy needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicOrgsDetails]);

  // Effects
  useEffect(() => {
    if (currentTask?.project) {
      return;
    }
    if (selectedOrg || isEditingAssignee) {
      fetchBasicUsers();
    } else {
      setSelectedAssignees([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, isEditingAssignee, currentTask?.project]);

  // Tracks the id of the most recent task we requested, so we can tell a "settled empty"
  // result for the CURRENT task apart from a stale result the persistent provider still
  // holds from a previously viewed task.
  const requestedTaskIdRef = useRef(null);

  useEffect(() => {
    if (currentTaskId) {
      requestedTaskIdRef.current = currentTaskId;
      fetchTask({
        variables: { where: { id: currentTaskId } },
      });
      fetchCollaborators();
      fetchHistories({
        variables: { where: { referenceId: currentTaskId, type: 'TASK' } },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTaskId]);

  useEffect(() => {
    if (currentTask?.id) {
      fetchHistories({
        variables: { where: { referenceId: currentTask.id, type: 'TASK' } },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTask]);

  document.title = currentTask && currentTask.name ? `Task: ${currentTask.name}` : 'Task Details';

  // Handlers
  const handleTabChange = (key) => {
    setActiveTab(key);

    if (key === '3' && currentTaskId) {
      fetchHistories({
        variables: {
          where: {
            referenceId: currentTaskId,
            type: 'TASK',
          },
        },
      });
    }
  };

  const handleStatusUpdate = async (selectedStatus) => {
    try {
      await updateTask({
        variables: {
          data: {
            status: selectedStatus,
          },
          where: { id: currentTaskId },
        },
      });
      // UPDATE_TASK returns the updated `status` field — Apollo's normalized
      // cache merges it into the task query automatically, so no refetchTask
      // is needed. Only the activity log needs an explicit refresh.
      await fetchHistories({
        variables: { where: { referenceId: currentTaskId } },
      });
      message.success('Task status updated successfully.');
    } catch (error) {
      message.error('Failed to update task status.');
      console.log('Update failed:', error);
    }
  };

  const handleUserUpdate = async () => {
    try {
      const orgUpdate = selectedOrg
        ? { connect: { id: selectedOrg } }
        : currentTask && currentTask.assigneeOrg
          ? { disconnect: true }
          : {};

      const activityLog = 'Updated Assignee';

      const newAssignee = selectedAssignees;
      let newCollaborators = [...selectedCollaborators];

      const currentAssignees = currentTask && currentTask.assigneeUsers;
      const currentAssigneeIds = currentAssignees.map((user) => user.id);
      const previousCollaborators =
        currentTask && currentTask.collaborators ? currentTask.collaborators : [];

      const assigneeUsers = {};
      const collaborators = {};
      const followers =
        currentTask && currentTask.followers ? currentTask.followers.map((user) => user.id) : [];

      // Auto-promote: if new assignee is a collaborator, auto-remove from collaborators
      if (selectedAssignees && selectedCollaborators.includes(selectedAssignees)) {
        newCollaborators = newCollaborators.filter(id => id !== selectedAssignees);
        setSelectedCollaborators(newCollaborators);
        message.info('Collaborator promoted to assignee.');
      }

      // Detect outgoing assignee for collaborator promotion
      const outgoingAssigneeId = currentAssigneeIds.length > 0 && selectedAssignees && selectedAssignees !== currentAssigneeIds[0]
        ? currentAssigneeIds[0]
        : null;
      const outgoingAssignee = outgoingAssigneeId
        ? currentAssignees.find(u => u.id === outgoingAssigneeId)
        : null;

      if (outgoingAssignee) {
        const addAsCollaborator = await new Promise((resolve) => {
          Modal.confirm({
            title: 'Add previous assignee as collaborator?',
            content: `Add ${startCase(outgoingAssignee.name || outgoingAssignee.username)} as a collaborator so they continue receiving updates on this ticket?`,
            okText: 'Yes, add as collaborator',
            cancelText: 'No, skip',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
          });
        });
        if (addAsCollaborator && !newCollaborators.includes(outgoingAssigneeId)) {
          newCollaborators = [...newCollaborators, outgoingAssigneeId];
        }
      }

      if (selectedAssignees && followers.includes(selectedAssignees)) {
        message.warning(
          'Assignees are already followers. Please remove them from followers first.'
        );
        return;
      }

      if (currentAssignees) {
        const disconnectIds = currentAssigneeIds.filter((id) => !newAssignee.includes(id));
        if (disconnectIds.length > 0) {
          assigneeUsers.disconnect = disconnectIds.map((id) => ({ id }));
        }
      }

      if (newAssignee.length > 0 && !currentAssigneeIds.includes(newAssignee[0])) {
        assigneeUsers.connect = { id: newAssignee };
      }

      const usersToConnect = newCollaborators.filter(
        (id) => !previousCollaborators.some((user) => user.id === id)
      );

      const usersToDisconnect = previousCollaborators.filter(
        (user) => !newCollaborators.includes(user.id)
      );

      if (usersToDisconnect.length > 0) {
        collaborators.disconnect = usersToDisconnect.map((user) => ({
          id: user.id,
        }));
      }

      if (usersToConnect.length > 0) {
        collaborators.connect = usersToConnect.map((id) => ({ id }));
      }

      // Handle team update
      const teamsUpdate = {};
      const currentTeamIds = (currentTask?.teams || []).map((t) => t.id);

      if (currentTeamIds.length > 0) {
        teamsUpdate.disconnect = currentTeamIds.map((id) => ({ id }));
      }
      if (selectedTeamId) {
        teamsUpdate.connect = [{ id: selectedTeamId }];
      }

      await updateTask({
        variables: {
          data: {
            assigneeOrg: orgUpdate,
            assigneeUsers,
            collaborators,
            ...(Object.keys(teamsUpdate).length > 0 ? { teams: teamsUpdate } : {}),
            activityLog,
          },
          where: { id: currentTaskId },
        },
      });

      await Promise.all([
        refetchTask(),
        fetchHistories({
          variables: { where: { referenceId: currentTaskId } },
        }),
      ]);
      message.success('Task assignee updated successfully.');
      setIsEditingAssignee(false);
    } catch (error) {
      message.error('Failed to update task assignee.');
      console.log('Update failed:', error);
    }
  };

  const handleDetailsFieldChange = (field, value) => {
    setDetailsForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'requestType' && value !== prev.requestType) {
        next.requestSubType = null;
      }
      if (field === 'channelPartnerId' && value !== prev.channelPartnerId) {
        next.advertiserId = null;
        setAdvertiserOrgs([]);
      }
      return next;
    });
  };

  const handleDetailsCancel = () => {
    setDetailsForm(buildDetailsFormFromTask(currentTask));
    setIsEditingDetails(false);
  };

  const handleDetailsUpdate = async () => {
    try {
      const {
        urgency,
        startDate,
        dueDate,
        requestType,
        requestSubType,
        channelPartnerId,
        advertiserId,
      } = detailsForm;

      if (!channelPartnerId || !advertiserId) {
        message.warning('Channel Partner and Advertiser are required.');
        return;
      }

      if (!(currentTask && currentTask.project) && !dueDate) {
        message.warning('Due Date is required when the task is not linked to a project.');
        return;
      }

      if (startDate && dueDate && !startDate.isBefore(dueDate, 'day')) {
        message.warning('Start date must be before due date.');
        return;
      }

      const buildOrgMutation = (nextId, currentRef) => {
        if (nextId) return { connect: { id: nextId } };
        if (currentRef) return { disconnect: true };
        return undefined;
      };

      const data = {
        urgency: urgency || null,
        startDate: startDate ? startDate.toISOString() : null,
        dueDate: dueDate ? dueDate.toISOString() : null,
        requestType: requestType || null,
        requestSubType: requestSubType || null,
        activityLog: 'Updated Details',
      };

      const channelPartnerMutation = buildOrgMutation(
        channelPartnerId,
        currentTask && currentTask.channelPartner
      );
      if (channelPartnerMutation) {
        data.channelPartner = channelPartnerMutation;
      }

      const advertiserMutation = buildOrgMutation(
        advertiserId,
        currentTask && currentTask.advertiser
      );
      if (advertiserMutation) {
        data.advertiser = advertiserMutation;
      }

      await updateTask({
        variables: {
          data,
          where: { id: currentTaskId },
        },
      });

      // UPDATE_TASK returns urgency, dates, requestType/subType, and
      // channelPartner/advertiser as {id, name} — all the fields edited here.
      // Apollo's cache merge handles the task refresh; only histories needs an
      // explicit refetch.
      await fetchHistories({
        variables: { where: { referenceId: currentTaskId } },
      });
      message.success('Task details updated successfully.');
      setIsEditingDetails(false);
    } catch (error) {
      message.error('Failed to update task details.');
      console.log('Update failed:', error);
    }
  };

  const handleCollaboratorsUpdate = async () => {
    if (!isCollaborator && (isCurrentUserAssignee || isFollowers)) {
      if (isCurrentUserAssignee) {
        message.warning('Assignees are already collaborators but with more responsibility.');
      } else if (isFollowers) {
        message.warning('Please unfollow first if you want to be a collaborator.');
      }
      return;
    }

    setIsCollaborator(!isCollaborator);
    if (!isCollaborator) {
      try {
        await updateTask({
          variables: {
            data: {
              collaborators: {
                connect: { id: currentUser.id },
              },
            },
            where: { id: currentTaskId },
          },
        });
        refetchTask();
        message.success('You are now a collaborator of the task.');
      } catch (error) {
        message.error('Failed to be a collaborator.');
      }
    } else {
      try {
        await updateTask({
          variables: {
            data: {
              collaborators: {
                disconnect: { id: currentUser.id },
              },
            },
            where: { id: currentTaskId },
          },
        });
        refetchTask();
        message.success('Left the task.');
      } catch (error) {
        message.error('Failed to leave the task.');
      }
    }
  };

  const handleFollowerModalConfirm = async () => {
    if (showFollowerModal) {
      try {
        message.loading('Removing follower...', 0);
        await updateTask({
          variables: {
            where: { id: currentTaskId },
            data: {
              followers: {
                disconnect: [{ id: selectedFollowerId }],
              },
            },
          },
        });
        refetchTask();
        message.destroy();
        message.success('Follower removed successfully');
        setShowFollowerModal(false);
      } catch (error) {
        message.error(`Failed to remove follower: ${error.message}`);
      }
    }
  };

  // Early returns
  const view = getTaskDetailsView({
    currentTaskId,
    currentTask,
    taskLoading,
    requestedTaskId: requestedTaskIdRef.current,
  });

  if (view === 'no-selection') return <p>Select a task to see the details.</p>;

  // Not-yet-started / in-flight / stale fetch — spinner instead of flashing. A
  // cache-and-network refetch keeps currentTask, so a matching task renders straight to
  // content (no flicker).
  if (view === 'loading') {
    return <OverlaySpinner />;
  }

  // Query settled with no matching task — render a friendly state instead of an endless
  // spinner (the original bug) or a one-frame "not found" flash on load/navigation.
  if (view === 'missing') {
    return (
      <div className="task-details-page">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            height: '60vh',
            textAlign: 'center',
          }}
        >
          <p style={{ fontSize: 16, marginBottom: 0 }}>
            {taskError
              ? 'Something went wrong while loading this task. Please try again.'
              : 'This task could not be found, or you do not have permission to view it.'}
          </p>
          <Button onClick={() => history.goBack()}>Back</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="task-details-page">
      <TaskDetailsHeader
        currentTask={currentTask}
        currentTaskId={currentTaskId}
        history={history}
        level={level}
        windowWidth={windowWidth}
        updateTaskLoading={updateTaskLoading}
        taskLoading={taskLoading}
        onStatusUpdate={handleStatusUpdate}
      />

      <PageContentLayout className="task-details-page-content">
        <div className="task-details-content">
          <TaskDetailsTabs
            currentTask={currentTask}
            currentTaskId={currentTaskId}
            currentUser={currentUser}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            histories={histories}
            loadingHistories={loadingHistories}
            windowWidth={windowWidth}
          />

          {/* Sidebar - hidden on mobile (content moved to bottom sheet) */}
          {!isMobile && (
            <Col className="task-details-sidebar">
              <TaskDetailsFollowers
                currentTask={currentTask}
                currentTaskId={currentTaskId}
                currentUser={currentUser}
                isFollowers={isFollowers}
                isCurrentUserAssignee={isCurrentUserAssignee}
                isCollaborator={isCollaborator}
                updateTaskLoading={updateTaskLoading}
                updateTask={updateTask}
                refetchTask={refetchTask}
                onFollowChange={setIsFollowers}
              />

              <TaskDetailsMetadata
                currentTask={currentTask}
                level={level}
                isEditingDetails={isEditingDetails}
                setIsEditingDetails={setIsEditingDetails}
                detailsForm={detailsForm}
                onDetailsFieldChange={handleDetailsFieldChange}
                onDetailsSave={handleDetailsUpdate}
                onDetailsCancel={handleDetailsCancel}
                updateTaskLoading={updateTaskLoading}
                channelPartnerOrgs={channelPartnerOrgs}
                advertiserOrgs={advertiserOrgs}
                basicOrgsDetailsLoading={basicOrgsDetailsLoading}
                fetchChannelPartnerOrgs={fetchChannelPartnerOrgs}
                fetchAdvertiserOrgs={fetchAdvertiserOrgs}
              />

              <TaskDetailsAssignees
                currentTask={currentTask}
                currentUser={currentUser}
                level={level}
                isEditingAssignee={isEditingAssignee}
                setIsEditingAssignee={setIsEditingAssignee}
                selectedOrg={selectedOrg}
                setSelectedOrg={setSelectedOrg}
                selectedAssignees={selectedAssignees}
                setSelectedAssignees={setSelectedAssignees}
                selectedCollaborators={selectedCollaborators}
                setSelectedCollaborators={setSelectedCollaborators}
                isCollaborator={isCollaborator}
                isCurrentUserAssignee={isCurrentUserAssignee}
                isFollowers={isFollowers}
                updateTaskLoading={updateTaskLoading}
                basicOrgsDetails={basicOrgsDetails}
                basicOrgsDetailsLoading={basicOrgsDetailsLoading}
                basicUsersDetails={basicUsersDetails}
                basicUsersDetailsLoading={basicUsersDetailsLoading}
                collaboratorsData={collaboratorsData}
                collaboratorsLoading={collaboratorsLoading}
                fetchBasicOrgs={fetchBasicOrgs}
                fetchBasicUsers={fetchBasicUsers}
                fetchCollaborators={fetchCollaborators}
                onUserUpdate={handleUserUpdate}
                onCollaboratorsUpdate={handleCollaboratorsUpdate}
                onFollowerSelected={(id) => {
                  setShowFollowerModal(true);
                  setSelectedFollowerId(id);
                }}
                teams={teams}
                teamsLoading={teamsLoading}
                selectedTeamId={selectedTeamId}
                setSelectedTeamId={setSelectedTeamId}
                fetchTeamsByOrg={fetchTeamsByOrg}
              />
            </Col>
          )}
        </div>
      </PageContentLayout>

      {/* Mobile: Floating Summary Pill */}
      {isMobile && (
        <TaskDetailsMobileSummary
          currentTask={currentTask}
          isSheetOpen={isSheetOpen}
          onToggle={toggleSheet}
        />
      )}

      {/* Mobile: Bottom Sheet with Details */}
      {isMobile && (
        <TaskDetailsBottomSheet
          isOpen={isSheetOpen}
          currentHeight={currentHeight}
          isDragging={isDragging}
          dragHandleProps={dragHandleProps}
          onClose={closeSheet}
        >
          <TaskDetailsSheetContent
            currentTask={currentTask}
            currentUser={currentUser}
            currentTaskId={currentTaskId}
            isFollowers={isFollowers}
            isCurrentUserAssignee={isCurrentUserAssignee}
            isCollaborator={isCollaborator}
            updateTaskLoading={updateTaskLoading}
            updateTask={updateTask}
            refetchTask={refetchTask}
            onFollowChange={setIsFollowers}
            onCollaboratorsUpdate={handleCollaboratorsUpdate}
            level={level}
            isEditingAssignee={isEditingAssignee}
            setIsEditingAssignee={setIsEditingAssignee}
            selectedOrg={selectedOrg}
            setSelectedOrg={setSelectedOrg}
            selectedAssignees={selectedAssignees}
            setSelectedAssignees={setSelectedAssignees}
            fetchBasicOrgs={fetchBasicOrgs}
            fetchBasicUsers={fetchBasicUsers}
            basicOrgsDetails={basicOrgsDetails}
            basicOrgsDetailsLoading={basicOrgsDetailsLoading}
            basicUsersDetails={basicUsersDetails}
            basicUsersDetailsLoading={basicUsersDetailsLoading}
            onUserUpdate={handleUserUpdate}
            teams={teams}
            teamsLoading={teamsLoading}
            selectedTeamId={selectedTeamId}
            setSelectedTeamId={setSelectedTeamId}
            fetchTeamsByOrg={fetchTeamsByOrg}
            isEditingDetails={isEditingDetails}
            setIsEditingDetails={setIsEditingDetails}
            detailsForm={detailsForm}
            onDetailsFieldChange={handleDetailsFieldChange}
            onDetailsSave={handleDetailsUpdate}
            onDetailsCancel={handleDetailsCancel}
            channelPartnerOrgs={channelPartnerOrgs}
            advertiserOrgs={advertiserOrgs}
            fetchChannelPartnerOrgs={fetchChannelPartnerOrgs}
            fetchAdvertiserOrgs={fetchAdvertiserOrgs}
          />
        </TaskDetailsBottomSheet>
      )}

      {/* Follower Promotion Modal */}
      {showFollowerModal && (
        <Modal
          centered
          footer={[
            <Button
              key="cancel"
              onClick={() => setShowFollowerModal(false)}
              disabled={updateTaskLoading}
            >
              Cancel
            </Button>,
            <Button
              key="ok"
              type="primary"
              disabled={updateTaskLoading}
              onClick={handleFollowerModalConfirm}
            >
              {updateTaskLoading && <LoadingOutlined />} Remove
            </Button>,
          ]}
          title={'Remove Follower'}
          visible={showFollowerModal}
        >
          <p>
            This user is a follower. Do you want to promote them as an assignee and remove them from
            followers?
          </p>
        </Modal>
      )}

      {/* Page-level floating comment input — available across all tabs */}
      <TaskFloatingCommentInput currentTaskId={currentTaskId} />
    </div>
  );
};

TaskDetailsContainer.propTypes = {
  history: PropTypes.object.isRequired,
};

export default withRouter(TaskDetailsContainer);
